import { useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

export default function Contact() {
  const [formData, setFormData] = useState({
    fullName: '',
    studentId: '',
    email: '',
    queryType: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const groupMembers = [
    {
      name: 'Kaleah Blackman',
      role: 'Project Manager & Payment Systems Coordinator',
      email: 'kaleah.blackman@mycavehill.uwi.edu',
      description: 'Oversees project coordination and payment processing inquiries'
    },
    {
      name: 'Aasiyah Brathwaite',
      role: 'Student Accounts Specialist',
      email: 'aasiyah.brathwaite@mycavehill.uwi.edu',
      description: 'Handles account balance and fee-related questions'
    },
    {
      name: 'Randall Roberts',
      role: 'Technical Support Lead',
      email: 'randall.roberts@mycavehill.uwi.edu',
      description: 'Assists with online payment platform and technical issues'
    },
    {
      name: 'Whennecia Lockhart',
      role: 'Banking & Transfer Specialist',
      email: 'whennecia.lockhart@mycavehill.uwi.edu',
      description: 'Specializes in bank transfers and international payments'
    },
    {
      name: 'Rashida Mayers',
      role: 'Deadline & Policy Advisor',
      email: 'rashida.mayers1@mycavehill.uwi.edu',
      description: 'Provides guidance on payment deadlines and university policies'
    }
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    // Validate form
    if (!formData.fullName || !formData.studentId || !formData.email || !formData.queryType || !formData.message) {
      setSubmitStatus('error');
      setIsSubmitting(false);
      return;
    }

    if (formData.message.length > 500) {
      setSubmitStatus('error');
      setIsSubmitting(false);
      return;
    }

    try {
      const formDataToSubmit = new URLSearchParams();
      formDataToSubmit.append('fullName', formData.fullName);
      formDataToSubmit.append('studentId', formData.studentId);
      formDataToSubmit.append('email', formData.email);
      formDataToSubmit.append('queryType', formData.queryType);
      formDataToSubmit.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d46m8arhnd8fvojd26ng', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formDataToSubmit
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          fullName: '',
          studentId: '',
          email: '',
          queryType: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative bg-gradient-to-r from-purple-600 to-purple-800 text-white py-16"
        style={{
          backgroundImage: `url('https://public.readdy.ai/ai/img_res/80c7b9cad7350f0af241a043760627b5.jpg')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-purple-900/80"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">Contact Student Accounts</h1>
          <p className="text-xl max-w-4xl leading-relaxed">
            If you require assistance, clarification on a transaction, or have a question about your fee status, please complete the contact form below. A Student Accounts representative will respond as soon as possible.
          </p>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Student Support Form</h2>
              <p className="text-lg text-gray-600">Fill out the form below and we'll get back to you promptly</p>
            </div>

            {submitStatus === 'success' && (
              <div className="mb-6 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded">
                <div className="flex items-center">
                  <i className="ri-check-circle-line mr-2"></i>
                  <span>Your message has been sent successfully! We'll respond within 24 hours.</span>
                </div>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="mb-6 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
                <div className="flex items-center">
                  <i className="ri-error-warning-line mr-2"></i>
                  <span>Please check all fields and ensure your message is under 500 characters.</span>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} data-readdy-form id="student-contact-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label htmlFor="fullName" className="block text-sm font-semibold text-gray-700 mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="fullName"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                    placeholder="Enter your full name"
                  />
                </div>

                <div>
                  <label htmlFor="studentId" className="block text-sm font-semibold text-gray-700 mb-2">
                    Student ID Number *
                  </label>
                  <input
                    type="text"
                    id="studentId"
                    name="studentId"
                    value={formData.studentId}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                    placeholder="e.g., 821012345"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                    placeholder="your.email@example.com"
                  />
                </div>

                <div>
                  <label htmlFor="queryType" className="block text-sm font-semibold text-gray-700 mb-2">
                    Query Type *
                  </label>
                  <div className="relative">
                    <select
                      id="queryType"
                      name="queryType"
                      value={formData.queryType}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm appearance-none pr-8"
                    >
                      <option value="">Select query type</option>
                      <option value="Payment">Payment</option>
                      <option value="Late Fee">Late Fee</option>
                      <option value="Refund">Refund</option>
                      <option value="Other">Other</option>
                    </select>
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                      <i className="ri-arrow-down-s-line text-gray-400"></i>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mb-6">
                <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                  Message *
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                  maxLength={500}
                  rows={6}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm resize-vertical"
                  placeholder="Please describe your question or issue in detail..."
                ></textarea>
                <div className="text-right text-sm text-gray-500 mt-1">
                  {formData.message.length}/500 characters
                </div>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-purple-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-purple-700 disabled:bg-purple-400 disabled:cursor-not-allowed transition-colors cursor-pointer whitespace-nowrap"
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center">
                    <i className="ri-loader-4-line animate-spin mr-2"></i>
                    Sending Message...
                  </span>
                ) : (
                  <span className="flex items-center justify-center">
                    <i className="ri-send-plane-line mr-2"></i>
                    Send Message
                  </span>
                )}
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* Group Contact Information */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Group Contact Information</h2>
            <p className="text-lg text-gray-600">Connect with our specialized team members for targeted assistance</p>
          </div>
          
          <div className="bg-white rounded-xl shadow-lg p-8">
            <h3 className="text-xl font-semibold text-gray-900 mb-8 text-center">Our Support Team</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {groupMembers.map((member, index) => (
                <div key={index} className="bg-gray-50 rounded-lg p-6 text-center hover:shadow-md transition-shadow">
                  {index === 0 ? (
                    <div className="w-16 h-16 rounded-full mx-auto mb-4 overflow-hidden">
                      <img 
                        src="https://static.readdy.ai/image/7acfccf655b34ed909342c128b73c861/f1474470776e6791abe53d486bbafc5a.jpeg"
                        alt={member.name}
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                  ) : index === 1 ? (
                    <div className="w-16 h-16 rounded-full mx-auto mb-4 overflow-hidden">
                      <img 
                        src="https://static.readdy.ai/image/7acfccf655b34ed909342c128b73c861/a246b57cff446bca1b4a9c274b3f3d5b.jpeg"
                        alt={member.name}
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                  ) : index === 2 ? (
                    <div className="w-16 h-16 rounded-full mx-auto mb-4 overflow-hidden">
                      <img 
                        src="https://static.readdy.ai/image/7acfccf655b34ed909342c128b73c861/f19c80d99d21f30c768f6e6e5b8e595d.jpeg"
                        alt={member.name}
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                  ) : index === 3 ? (
                    <div className="w-16 h-16 rounded-full mx-auto mb-4 overflow-hidden">
                      <img 
                        src="https://static.readdy.ai/image/7acfccf655b34ed909342c128b73c861/3d8fd4ed5fc75714fcfa144e9296a7ff.jpeg"
                        alt={member.name}
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                  ) : (
                    <div className="w-16 h-16 rounded-full mx-auto mb-4 overflow-hidden">
                      <img 
                        src="https://static.readdy.ai/image/7acfccf655b34ed909342c128b73c861/c9d1dcefded1d93b07130d08fa804102.jpeg"
                        alt={member.name}
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                  )}

                  <h4 className="text-lg font-semibold text-gray-900 mb-2">{member.name}</h4>
                  <div className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm font-medium mb-3">
                    {member.role}
                  </div>
                  <p className="text-gray-600 text-sm mb-4">{member.description}</p>
                  <a
                    href={`mailto:${member.email}`}
                    className="inline-flex items-center text-purple-600 hover:text-purple-800 font-medium text-sm cursor-pointer"
                  >
                    <i className="ri-mail-line mr-2"></i>
                    Contact {member.name.split(' ')[0]}
                  </a>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
            <p className="text-lg text-gray-600">Quick answers to common payment questions</p>
          </div>
          
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">How long does it take for payments to be processed?</h3>
              <p className="text-gray-600">Bank transfers typically take 1-3 business days to process. We recommend submitting payments at least 7 days before the deadline.</p>
            </div>
            
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">What happens if I miss the payment deadline?</h3>
              <p className="text-gray-600">Late fees will be applied starting October 5th, 2025. If payment is not received by November 15th, 2025, you may be deregistered from your courses.</p>
            </div>
            
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Can I pay in installments?</h3>
              <p className="text-gray-600">Please contact Student Accounts directly to discuss payment plan options. Use the form above or email one of our team members.</p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
